import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import LoadingSpinner from "../LoadingSpinner";
import { accountApi } from "../../services/api";
import { getErrorMessage } from "../../utils/formatters";
import { MODEL_FAMILIES } from "../../utils/constants";

const EMPTY_VALUES = Object.fromEntries(
  MODEL_FAMILIES.map(({ key }) => [key, ""]),
);

/**
 * Per-family low-balance warning threshold settings.
 * Saves via PUT /api/account/credit-thresholds.
 */
const ThresholdSettings = ({ thresholds, loading, onSaved, toast }) => {
  const [values, setValues] = useState(EMPTY_VALUES);
  const [saving, setSaving] = useState(false);

  // The last-saved snapshot, used for dirty-state comparison
  const [savedSnapshot, setSavedSnapshot] = useState(EMPTY_VALUES);

  // Sync form from prop whenever thresholds load/change
  useEffect(() => {
    if (!thresholds) return;
    const synced = Object.fromEntries(
      MODEL_FAMILIES.map(({ key }) => [
        key,
        thresholds[key] == null ? "" : String(thresholds[key]),
      ]),
    );
    setValues(synced);
    setSavedSnapshot(synced);
  }, [thresholds]);

  const isDirty = useMemo(
    () => MODEL_FAMILIES.some(({ key }) => values[key] !== savedSnapshot[key]),
    [values, savedSnapshot],
  );

  const handleChange = (family, raw) => {
    // Allow empty (disabled) or non-negative integers.
    // parseInt filters out decimals typed into the number input (UX-5: step="1"
    // only affects the spinner arrows, not keyboard entry).
    if (raw === "") {
      setValues((prev) => ({ ...prev, [family]: "" }));
      return;
    }
    const n = Number.parseInt(raw, 10);
    if (!Number.isNaN(n) && n >= 0) {
      setValues((prev) => ({ ...prev, [family]: String(n) }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const { key } of MODEL_FAMILIES) {
        // SEC-1: parseInt on a validated numeric string cannot produce NaN here
        // because handleChange only stores "" or stringified non-negative integers.
        payload[key] =
          values[key] === "" ? null : Number.parseInt(values[key], 10);
      }
      await accountApi.updateCreditThresholds(payload);
      toast.success("Warning thresholds updated");
      setSavedSnapshot({ ...values });
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save thresholds"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section">
      <h2 className="settings-section-title">Low Balance Warnings</h2>
      <div className="settings-card">
        {loading ? (
          <LoadingSpinner size="small" text="Loading thresholds..." />
        ) : (
          <>
            <p className="threshold-description">
              Get a warning when your credit balance drops to or below these
              levels. Leave blank to disable warnings for a family.
            </p>
            <div className="threshold-grid">
              {MODEL_FAMILIES.map(({ key, label }) => (
                <div key={key} className="threshold-field">
                  <label
                    className="threshold-label"
                    htmlFor={`threshold-${key}`}
                  >
                    {label}
                  </label>
                  <input
                    id={`threshold-${key}`}
                    className="threshold-input"
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Disabled"
                    value={values[key]}
                    onChange={(e) => handleChange(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <button
              className="threshold-save-btn"
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? "Saving..." : "Save Thresholds"}
            </button>
          </>
        )}
      </div>
    </section>
  );
};

ThresholdSettings.propTypes = {
  thresholds: PropTypes.shape(
    Object.fromEntries(
      MODEL_FAMILIES.map(({ key }) => [key, PropTypes.number]),
    ),
  ),
  loading: PropTypes.bool,
  onSaved: PropTypes.func,
  toast: PropTypes.shape({
    success: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }).isRequired,
};

export default ThresholdSettings;
